import { useMindlerApi } from "api/apiv2";
import CONSTANTS from "core/constants";
import { type NewSlot, type SlotApiRes, type Template } from "core/types";
import { SlotMethod } from "@mindlercare/schema";
import { useUserHasRole } from "hooks/userHooks";
import {
  isSlotOverlapping,
  isSlotInThePast,
  isSlotWithinTwoHours,
} from "components/booking/booking.utils";
import { add, addMinutes, getUnixTime } from "date-fns";
import { type RouterInput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQueryClient } = ReactQueryV5;

import { type TFunction } from "i18next";
import { useAlertContext } from "../AlertContext";
import { useCalendarSlots } from "./useCalendarSlots";
import { useInvalidateSlots } from "./useInvalidateSlots";
import { useServiceWindows, overlapsServiceWindow } from "./useServiceWindows";
import { useCallback } from "react";

const slotsAllowedWithinTwoHours = [
  CONSTANTS.SLOT_TEMPLATES.DIAGNOSTICS_JOURNAL_ENTRY,
  CONSTANTS.SLOT_TEMPLATES.BLOCKER,
  CONSTANTS.SLOT_TEMPLATES.FEEDBACK,
];

export const useHandleCreateSlot = () => {
  const { showAlert } = useAlertContext();
  const { data: slots = [] } = useCalendarSlots();
  const { data: serviceWindows = [] } = useServiceWindows();
  const isAdmin = useUserHasRole("Admin");
  const queryClient = useQueryClient();
  const api = useMindlerApi();
  const invalidateSlots = useInvalidateSlots();

  return useCallback(
    async (
      psychologistUserId: number,
      slot: NewSlot | null,
      t: TFunction,
      createSlot: (
        args: RouterInput["slots"]["psychologist"]["create"],
      ) => Promise<{ slotId: number }>,
      templates: Template[],
      selectedTemplate: Template,
    ) => {
      if (slot?.action === "doubleClick") return;
      if (!templates?.length || !slot) return;

      const shouldBlockSlotInThePast = isSlotInThePast(
        slot.start,
        selectedTemplate,
      );

      if (shouldBlockSlotInThePast) {
        showAlert(t("BOOK_SLOT_IN_THE_PAST"));
        return;
      }
      if (!selectedTemplate) {
        showAlert(t("PLEASE_SELECT_TEMPLATE_FIRST"));
        return;
      }

      slot.start.setSeconds(0, 0);
      const slotToAdd = {
        startsAt: getUnixTime(slot.start),
        endsAt: getUnixTime(
          add(slot.start, { minutes: selectedTemplate?.defaultLength }),
        ),
        templateId: selectedTemplate?.templateId ?? 1,
        method: CONSTANTS.MEETING_METHOD.VIDEO as SlotMethod,
      };

      const shouldAllowNewSlot = isSlotOverlapping(slots, templates, slotToAdd);

      if (!shouldAllowNewSlot) {
        showAlert(t("API_ERROR_SLOT_CANNOT_OVERLAP"));
        return;
      }

      if (
        overlapsServiceWindow(
          serviceWindows,
          slotToAdd.startsAt,
          slotToAdd.endsAt,
        )
      ) {
        showAlert(t("API_ERROR_SLOT_BLOCKED_BY_SERVICE_WINDOW"));
        return;
      }

      const slotIsAllowedWithinTwoHours = slotsAllowedWithinTwoHours.includes(
        selectedTemplate.name,
      );
      const isWithinTwoHours =
        !slotIsAllowedWithinTwoHours &&
        !isAdmin &&
        isSlotWithinTwoHours(slot.start);

      if (isWithinTwoHours) {
        showAlert(t("API_ERROR_LESS_THAN_TWO_HOURS_BEFORE_START"));
        return;
      }

      const tempSlot = {
        ...slotToAdd,
        status: CONSTANTS.BOOKING_STATUS.OPEN,
      };

      queryClient.setQueriesData<SlotApiRes[]>(
        api.slots.psychologist.calendar.queryFilter(),
        (old) => {
          if (!old) return old;
          return [...old, tempSlot as SlotApiRes];
        },
      );

      try {
        await createSlot({
          startsAt: slot.start,
          endsAt: addMinutes(slot.start, selectedTemplate.defaultLength),
          templateId: selectedTemplate.templateId,
          psychologistUserId,
        });
      } catch (err: unknown) {
        if ((err as { errorLangKey?: string })?.errorLangKey) {
          showAlert(t((err as { errorLangKey: string }).errorLangKey));
        }
      } finally {
        await invalidateSlots();
      }
    },
    [
      showAlert,
      queryClient,
      api,
      invalidateSlots,
      slots,
      serviceWindows,
      isAdmin,
    ],
  );
};
